/**
 * Templates de metas pré-configuradas para academias.
 * 
 * O gestor seleciona um template como ponto de partida
 * e pode customizar livremente os valores.
 * 
 * Cada template contém goals e weights para os 3 grupos de cargo.
 */

export const GOAL_TEMPLATES = {
    starter: {
        label: "Academia Iniciante",
        description: "Metas conservadoras para operações em fase de estruturação",
        icon: "mdi-sprout",
        color: "#34c38f",
        groups: {
            professor: {
                goals: {
                    trialConversion: 50,
                    attendanceRate: 70,
                    assiduidade: 95,
                    ocupacaoTurmas: 40,
                    evaluationCoverage: 10,
                    retentionRate: 80,
                },
                weights: {
                    trialConversion: 0.20,
                    attendanceRate: 0.15,
                    assiduidade: 0.15,
                    ocupacaoTurmas: 0.20,
                    evaluationCoverage: 0.10,
                    retentionRate: 0.20,
                },
            },
            consultor: {
                goals: {
                    revenue: 15000,
                    renewalRate: 70,
                    conversionRate: 30,
                    salesNew: 5,
                    tasksOnTime: 70,
                    trialScheduled: 8,
                },
                weights: {
                    revenue: 0.25,
                    renewalRate: 0.20,
                    conversionRate: 0.20,
                    salesNew: 0.15,
                    tasksOnTime: 0.15,
                    trialScheduled: 0.05,
                },
            },
            gerente: {
                goals: {
                    revenue: 30000,
                    activeMembers: 80,
                    salesCount: 15,
                    ticketMedio: 200,
                    cancellations: 5,
                    churnRate: 8,
                    renewalRate: 70,
                    crescimentoLiquido: 10,
                    ocupacaoTurmas: 50,
                },
                weights: {
                    revenue: 0.20,
                    activeMembers: 0.10,
                    salesCount: 0.10,
                    ticketMedio: 0.05,
                    cancellations: 0.15,
                    churnRate: 0.10,
                    renewalRate: 0.05,
                    crescimentoLiquido: 0.10,
                    ocupacaoTurmas: 0.15,
                },
            },
        },
    },
    growth: {
        label: "Crescimento",
        description: "Metas equilibradas para academias em expansão",
        icon: "mdi-chart-line",
        color: "#556ee6",
        groups: {
            professor: {
                goals: {
                    trialConversion: 65,
                    attendanceRate: 75,
                    assiduidade: 95,
                    ocupacaoTurmas: 60,
                    evaluationCoverage: 25,
                    retentionRate: 85,
                },
                weights: {
                    trialConversion: 0.20,
                    attendanceRate: 0.15,
                    assiduidade: 0.15,
                    ocupacaoTurmas: 0.20,
                    evaluationCoverage: 0.10,
                    retentionRate: 0.20,
                },
            },
            consultor: {
                goals: {
                    revenue: 30000,
                    renewalRate: 80,
                    conversionRate: 45,
                    salesNew: 10,
                    tasksOnTime: 85,
                    trialScheduled: 15,
                },
                weights: {
                    revenue: 0.25,
                    renewalRate: 0.20,
                    conversionRate: 0.20,
                    salesNew: 0.15,
                    tasksOnTime: 0.15,
                    trialScheduled: 0.05,
                },
            },
            gerente: {
                goals: {
                    revenue: 60000,
                    activeMembers: 150,
                    salesCount: 30,
                    ticketMedio: 350,
                    cancellations: 8,
                    churnRate: 5,
                    renewalRate: 80,
                    crescimentoLiquido: 22,
                    ocupacaoTurmas: 65,
                },
                weights: {
                    revenue: 0.20,
                    activeMembers: 0.10,
                    salesCount: 0.10,
                    ticketMedio: 0.05,
                    cancellations: 0.15,
                    churnRate: 0.10,
                    renewalRate: 0.05,
                    crescimentoLiquido: 0.10,
                    ocupacaoTurmas: 0.15,
                },
            },
        },
    },
    highPerformance: {
        label: "Alta Performance",
        description: "Metas agressivas para equipes de alto rendimento",
        icon: "mdi-rocket-launch",
        color: "#f1b44c",
        groups: {
            professor: {
                goals: {
                    trialConversion: 80,
                    attendanceRate: 90,
                    assiduidade: 98,
                    ocupacaoTurmas: 80,
                    evaluationCoverage: 40,
                    retentionRate: 90,
                },
                weights: {
                    trialConversion: 0.20,
                    attendanceRate: 0.15,
                    assiduidade: 0.15,
                    ocupacaoTurmas: 0.20,
                    evaluationCoverage: 0.10,
                    retentionRate: 0.20,
                },
            },
            consultor: {
                goals: {
                    revenue: 50000,
                    renewalRate: 90,
                    conversionRate: 60,
                    salesNew: 15,
                    tasksOnTime: 95,
                    trialScheduled: 25,
                },
                weights: {
                    revenue: 0.25,
                    renewalRate: 0.20,
                    conversionRate: 0.20,
                    salesNew: 0.15,
                    tasksOnTime: 0.15,
                    trialScheduled: 0.05,
                },
            },
            gerente: {
                goals: {
                    revenue: 100000,
                    activeMembers: 300,
                    salesCount: 50,
                    ticketMedio: 500,
                    cancellations: 10,
                    churnRate: 3,
                    renewalRate: 90,
                    crescimentoLiquido: 40,
                    ocupacaoTurmas: 80,
                },
                weights: {
                    revenue: 0.20,
                    activeMembers: 0.10,
                    salesCount: 0.10,
                    ticketMedio: 0.05,
                    cancellations: 0.15,
                    churnRate: 0.10,
                    renewalRate: 0.05,
                    crescimentoLiquido: 0.10,
                    ocupacaoTurmas: 0.15,
                },
            },
        },
    },
};

export const GOAL_TEMPLATE_OPTIONS = Object.entries(GOAL_TEMPLATES).map(([key, t]) => ({
    value: key,
    label: t.label,
    description: t.description,
    icon: t.icon,
    color: t.color,
}));
